import React from "react";
import { NavLink } from "react-router";
import styled from "styled-components";
import { useAuth } from "../../auth/useAuth";
import AdminOnly from "../permissions/AdminOnly";
import SignOutButton from "../auth/SignOutButton";

const cifLogoSrc = `${process.env.PUBLIC_URL ?? ""}/ciflogo.jpeg`;

const Bar = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 56px;
  background: #ffffff;
  border-bottom: 1px solid #e8eaed;
  margin-bottom: 32px;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Logo = styled.img`
  height: 36px;
  width: 36px;
  object-fit: contain;
  display: block;
`;

const AppTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  letter-spacing: -0.3px;
  margin-right: 24px;
`;

const NavLinkItem = styled(NavLink)`
  font-size: 14px;
  font-weight: 500;
  color: #555;
  text-decoration: none;
  padding: 6px 12px;
  border-radius: 6px;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: #f5f5f5;
    color: #1a1a2e;
  }

  &.active {
    background: #e8f0fe;
    color: #1a56db;
    font-weight: 600;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserLabel = styled.span`
  font-size: 13px;
  color: #666;
`;

const RoleBadge = styled.span<{ $role: string }>`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 3px 8px;
  border-radius: 6px;
  background: ${(props) => (props.$role === "admin" ? "#e3f2fd" : "#f5f5f5")};
  color: ${(props) => (props.$role === "admin" ? "#1565c0" : "#555")};
`;

// =============================================
// Shared top navigation bar rendered at the top
// of every authenticated page.
//
// Navigation links:
//   - "History" is visible to all authenticated users.
//   - "Upload"  is visible only to admins (wrapped
//     in AdminOnly so it renders null for viewers).
//
// The right side shows the user's display name,
// their role badge, and the sign-out button.
// =============================================
const TopNavBar: React.FC = () => {
  const { userProfile } = useAuth();

  const displayName =
    userProfile?.firstName && userProfile?.lastName
      ? `${userProfile.firstName} ${userProfile.lastName}`
      : (userProfile?.firstName ?? "User");

  return (
    <Bar>
      <LeftSection>
        <Logo src={cifLogoSrc} alt="CIF Machine Hours" />
        <AppTitle>CIF Machine Hours</AppTitle>

        <NavLinkItem to="/history">History</NavLinkItem>

        <AdminOnly>
          <NavLinkItem to="/upload">Upload</NavLinkItem>
        </AdminOnly>
      </LeftSection>

      <RightSection>
        <UserLabel>{displayName}</UserLabel>
        {userProfile && (
          <RoleBadge $role={userProfile.role}>{userProfile.role}</RoleBadge>
        )}
        <SignOutButton />
      </RightSection>
    </Bar>
  );
};

export default TopNavBar;
